import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Flex,
  Heading,
  HStack,
  IconButton,
  Badge,
  VStack,
  Text,
  Box,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, ViewIcon, PhoneIcon, EmailIcon, LinkIcon } from '@chakra-ui/icons';
import { Client } from '../../api/clientApi';
import { PermissionGuard } from '../PermissionGuard';
import { Permission } from '../../utils/permissions';

interface ClientCardProps {
  client: Client;
  onView: (client: Client) => void;
  onEdit: (client: Client) => void;
  onDelete: (client: Client) => void;
}

const ClientCard: React.FC<ClientCardProps> = ({ client, onView, onEdit, onDelete }) => {
  // Debug: log the project field to see what is being received
  console.log('ClientCard project field:', client.clientName, client.project);

  // Normalize project array to count all valid project references
  const projectCount = Array.isArray(client.project)
    ? client.project.filter(p => {
        if (typeof p === 'string') return true;
        if (p && typeof p === 'object') {
          if ('_id' in p && typeof (p as any)._id === 'string') return true;
          // Handle MongoDB Extended JSON: { $oid: string }
          if ('$oid' in p && typeof (p as any).$oid === 'string') return true;
        }
        return false;
      }).length
    : 0;

  // Get contact info availability
  const hasEmail = !!client.additionalInformation?.email;
  const hasPhone = !!client.additionalInformation?.phone;
  const hasLeadPerson = !!client.additionalInformation?.leadPersonName;

  const hasWebsite = !!client.additionalInformation?.websiteUrl?.trim();

  const completionFields = [
    client.clientName,
    client.additionalInformation?.leadPersonName,
    client.additionalInformation?.email,
    client.additionalInformation?.phone,
    client.additionalInformation?.secondaryContact,
    client.additionalInformation?.websiteUrl,
  ].filter(Boolean).length;
  const completionPercentage = Math.round((completionFields / 6) * 100);

  // Get relative time since creation
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} week${Math.ceil(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} month${Math.ceil(diffDays / 30) > 1 ? 's' : ''} ago`;
    return `${Math.ceil(diffDays / 365)} year${Math.ceil(diffDays / 365) > 1 ? 's' : ''} ago`;
  };

  return (
    <Card
      variant="elevated"
      boxShadow="lg"
      borderRadius="xl"
      transition="all 0.2s ease-in-out"
      _hover={{ 
        transform: 'translateY(-4px) scale(1.02)', 
        boxShadow: '2xl', 
        borderColor: 'blue.400',
      }}
      borderWidth={2}
      borderColor="gray.100"
      bgGradient="linear(to-br, white, blue.25)"
      position="relative"
      overflow="hidden"
    >
      {/* Completion indicator */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="4px"
        bg="gray.100"
      >
        <Box
          height="100%"
          bg={completionPercentage >= 80 ? 'green.400' : completionPercentage >= 60 ? 'yellow.400' : 'red.400'}
          width={`${completionPercentage}%`}
          transition="width 0.3s ease"
        />
      </Box>

      <CardHeader pb={2} pt={6}>
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={0} flex={1}>
            <Tooltip label={client.clientName} hasArrow>
              <Heading size="md" noOfLines={1} color="blue.700" mb={1}>
                {client.clientName}
              </Heading>
            </Tooltip>
            <HStack spacing={2} wrap="wrap">
              <Badge 
                colorScheme={projectCount > 0 ? 'blue' : 'gray'} 
                fontSize="0.8em" 
                px={2} 
                py={0.5} 
                borderRadius="md"
              >
                {projectCount} Project{projectCount !== 1 ? 's' : ''}
              </Badge>
              <Badge 
                colorScheme={completionPercentage >= 80 ? 'green' : completionPercentage >= 60 ? 'yellow' : 'red'}
                fontSize="0.8em" 
                px={2} 
                py={0.5} 
                borderRadius="md"
                variant="subtle"
              >
                {completionPercentage}% Complete
              </Badge>
            </HStack>
          </VStack>
          <HStack spacing={1}>
            <Tooltip label="View Details" hasArrow>
              <IconButton
                aria-label="View client details"
                icon={<ViewIcon />}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                onClick={() => onView(client)}
                _hover={{ bg: 'blue.100' }}
              />
            </Tooltip>
            <PermissionGuard permission={Permission.EDIT_CLIENTS}>
              <Tooltip label="Edit Client" hasArrow>
                <IconButton
                  aria-label="Edit client"
                  icon={<EditIcon />}
                  size="sm"
                  variant="ghost"
                  colorScheme="yellow"
                  onClick={() => onEdit(client)}
                  _hover={{ bg: 'yellow.100' }}
                />
              </Tooltip>
            </PermissionGuard>
            <PermissionGuard permission={Permission.DELETE_CLIENTS}>
              <Tooltip label="Delete Client" hasArrow>
                <IconButton
                  aria-label="Delete client"
                  icon={<DeleteIcon />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => onDelete(client)}
                  _hover={{ bg: 'red.100' }}
                />
              </Tooltip>
            </PermissionGuard>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody pt={2}>
        <VStack align="start" spacing={3} fontSize="sm">
          {/* Contact Information */}
          {hasLeadPerson && (
            <Text color="gray.700" noOfLines={1}>
              <Text as="span" fontWeight="semibold">Lead:</Text> {client.additionalInformation.leadPersonName}
            </Text>
          )}
          
          {/* Contact badges */}
          <Wrap spacing={1}>
            {hasEmail && (
              <WrapItem>
                <Tooltip label={client.additionalInformation.email} hasArrow>
                  <Badge colorScheme="green" variant="subtle" display="flex" alignItems="center" gap={1}>
                    <EmailIcon boxSize={2} />
                    Email
                  </Badge>
                </Tooltip>
              </WrapItem>
            )}
            {hasPhone && (
              <WrapItem>
                <Tooltip label={client.additionalInformation.phone} hasArrow>
                  <Badge colorScheme="blue" variant="subtle" display="flex" alignItems="center" gap={1}>
                    <PhoneIcon boxSize={2} />
                    Phone
                  </Badge>
                </Tooltip>
              </WrapItem>
            )}
            {hasWebsite && (
              <WrapItem>
                <Tooltip label={client.additionalInformation.websiteUrl} hasArrow>
                  <Badge colorScheme="purple" variant="subtle" display="flex" alignItems="center" gap={1}>
                    <LinkIcon boxSize={2} />
                    Website
                  </Badge>
                </Tooltip>
              </WrapItem>
            )}
          </Wrap>

          <Flex justify="space-between" align="center" w="100%" pt={2} borderTop="1px" borderColor="gray.100">
            <Text color="gray.500" fontSize="xs">
              Created {getRelativeTime(client.createdAt)}
            </Text>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default ClientCard;
